import {
  forwardRef,
  type ComponentPropsWithoutRef,
  type ElementRef,
  type PropsWithChildren,
} from "react";
import * as RadixSelect from "@radix-ui/react-select";

import { flattenStyles, platformProps } from "../../lib";
import type {
  SelectContentPrimitiveProps,
  SelectItemPrimitiveProps,
  SelectRootPrimitiveProps,
  SelectTriggerPrimitiveProps,
  SelectValuePrimitiveProps,
} from "./types";

/**
 * Select Root - Web implementation (Radix)
 *
 * Unstyled primitive. Styling is applied by the atom layer.
 * Radix takes the option's value string; `""` is its controlled "nothing selected".
 */
const SelectRoot = ({
  value,
  onValueChange,
  onOpenChange,
  disabled,
  name,
  children,
}: PropsWithChildren<SelectRootPrimitiveProps>) => (
  <RadixSelect.Root
    value={value?.value ?? ""}
    onValueChange={onValueChange}
    onOpenChange={onOpenChange}
    disabled={disabled}
    name={name}
  >
    {children}
  </RadixSelect.Root>
);

/**
 * Select Trigger - Web implementation (Radix)
 *
 * The interactive button that opens the dropdown.
 */
const SelectTrigger = forwardRef<
  ElementRef<typeof RadixSelect.Trigger>,
  SelectTriggerPrimitiveProps & ComponentPropsWithoutRef<typeof RadixSelect.Trigger>
>(
  (
    {
      className,
      children,
      testID,
      id,
      accessibilityLabel,
      disabled,
      onTriggerLayout: _onTriggerLayout,
      ...props
    },
    ref,
  ) => (
    <RadixSelect.Trigger
      ref={ref}
      className={className}
      disabled={disabled}
      aria-label={accessibilityLabel}
      {...platformProps({ testID, id, slot: "select-trigger" })}
      {...props}
    >
      {children}
    </RadixSelect.Trigger>
  ),
);

SelectTrigger.displayName = "SelectTrigger";

/**
 * Select Value - Web implementation (Radix)
 *
 * Radix v2 explicitly strips `className` from Select.Value's props before spreading
 * onto the internal span, so we wrap it in our own span to apply styling via CSS inheritance.
 * Styling (Typography) is applied at the atom layer.
 */
const SelectValue = ({ placeholder, className }: SelectValuePrimitiveProps) => (
  <span className={className}>
    <RadixSelect.Value placeholder={placeholder} />
  </span>
);

/**
 * Select Content - Web implementation (Radix)
 *
 * Combines Portal + Content + scrollable Viewport.
 */
const SelectContent = forwardRef<
  ElementRef<typeof RadixSelect.Content>,
  SelectContentPrimitiveProps
>(({ className, children, scrollHeight = 0, portalHost: _portalHost, style }, ref) => (
  <RadixSelect.Portal>
    <RadixSelect.Content
      ref={ref}
      position="popper"
      className={className}
      style={flattenStyles([{ minWidth: "var(--radix-select-trigger-width)" }, style])}
    >
      {/* Capped only by `scrollHeight`, as on native */}
      <RadixSelect.Viewport
        className="overflow-y-auto"
        style={scrollHeight > 0 ? { maxHeight: scrollHeight } : undefined}
      >
        {children}
      </RadixSelect.Viewport>
    </RadixSelect.Content>
  </RadixSelect.Portal>
));

SelectContent.displayName = "SelectContent";

/**
 * Select Item - Web implementation (Radix)
 *
 * A single selectable option in the dropdown.
 * `label` is rendered via ItemText so it appears in the trigger when selected.
 */
const SelectItem = forwardRef<
  ElementRef<typeof RadixSelect.Item>,
  SelectItemPrimitiveProps & ComponentPropsWithoutRef<typeof RadixSelect.Item>
>(({ value, label, disabled, className, testID, children, ...props }, ref) => (
  <RadixSelect.Item
    ref={ref}
    value={value}
    disabled={disabled}
    className={className}
    {...platformProps({ testID })}
    {...props}
  >
    {/* Wrapped in sr-only span because Radix strips className from ItemText itself.
        Radix still reads the text content for the trigger value display. */}
    <span className="sr-only">
      <RadixSelect.ItemText>{label}</RadixSelect.ItemText>
    </span>
    {children}
  </RadixSelect.Item>
));

SelectItem.displayName = "SelectItem";

export { SelectRoot, SelectTrigger, SelectValue, SelectContent, SelectItem };
